import { useCallback, useEffect, useState } from "react";

import { pyodideManager } from "@/components/pyodide/PyodideManager";

import { buildNumpyCode } from "./buildNumpyCode";
import { buildPlotlyFigure } from "./buildPlotlyFigure";
import { CLIENT_ONLY_CHART_TYPES, TYPES_2D } from "./constants";
import {
  extractNumericData,
  extractNumericData2D,
  extractNumericData2DFromTiptap,
  type AnalysisResult,
  type AnalysisType,
  type ChartOptions,
  type ChartTheme,
  type DataSource,
} from "./dataAnalysisUtils";

import type {
  CorrelationComputeResult,
  FFTComputeResult,
  HistogramComputeResult,
  PolyFitComputeResult,
} from "./buildPlotlyFigure";

type UseDataAnalysisReturn = {
  result: AnalysisResult | null;
  isLoading: boolean;
  error: string | null;
  runAnalysis: (type: AnalysisType, computeOptions?: ChartOptions) => Promise<void>;
};

export function extractData(
  source: DataSource,
  is2DType: boolean,
): { data: number[] | number[][]; error: null } | { data: null; error: string } {
  if (is2DType) {
    let data2d: number[][] | null = null;
    if (source.kind === "matrix") {
      data2d = extractNumericData2D(source.node);
    } else if (source.kind === "tiptap-table") {
      data2d = extractNumericData2DFromTiptap(source.node, source.doc);
    }
    if (!data2d) {
      return { data: null, error: "All cells must contain numeric values for this analysis type." };
    }
    return { data: data2d, error: null };
  }

  const data1d = extractNumericData(source);
  if (data1d.length < 2) {
    return { data: null, error: "At least 2 numeric data points are required." };
  }
  return { data: data1d, error: null };
}

export function buildNumpyFigureInput(analysisType: AnalysisType, parsed: unknown) {
  switch (analysisType) {
    case "histogram":
      return { chartType: "histogram" as const, computed: parsed as HistogramComputeResult };
    case "fft":
      return { chartType: "fft" as const, computed: parsed as FFTComputeResult };
    case "polyFit":
      return { chartType: "polyFit" as const, computed: parsed as PolyFitComputeResult };
    case "correlation":
      return { chartType: "correlation" as const, computed: parsed as CorrelationComputeResult };
    default:
      return null;
  }
}

export function useDataAnalysis(
  source: DataSource | null,
  theme: ChartTheme = "dark",
): UseDataAnalysisReturn {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nodeId = source?.kind === "tiptap-table" ? source.node : (source?.node.id ?? null);

  useEffect(() => {
    setResult(null);
    setError(null);
  }, [nodeId]);

  const runAnalysis = useCallback(
    async (analysisType: AnalysisType, computeOptions?: ChartOptions) => {
      if (!source) return;

      const is2DType = TYPES_2D.has(analysisType);
      const extracted = extractData(source, is2DType);
      if (extracted.data === null) {
        setError(extracted.error);
        return;
      }
      const { data } = extracted;

      // Client-only chart types need no Pyodide.
      if (CLIENT_ONLY_CHART_TYPES.has(analysisType)) {
        setIsLoading(true);
        setError(null);

        try {
          const figureInput = is2DType
            ? {
                chartType: analysisType as "heatmap" | "surface" | "scatter3d" | "contour",
                data2d: data as number[][],
              }
            : {
                chartType: analysisType as "boxplot" | "scatter" | "line" | "bar",
                data: data as number[],
              };

          const { plotlyData, plotlyLayout } = buildPlotlyFigure(figureInput, theme);
          setResult({
            type: analysisType,
            data: { plotlyData, plotlyLayout, chartType: analysisType },
          } as AnalysisResult);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Analysis failed");
          setResult(null);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Everything below requires numpy: statistics plus histogram/fft.
      if (!pyodideManager.isInitialized) {
        setError("Data analysis engine is still loading. Please try again in a moment.");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const code = buildNumpyCode(data, analysisType, computeOptions);
        if (!code) {
          setError("This analysis type is not supported for the selected data.");
          return;
        }

        const response = await pyodideManager.executeCode(code);
        const parsed = JSON.parse(response.result);

        const figureInput = buildNumpyFigureInput(analysisType, parsed);
        if (figureInput) {
          const { plotlyData, plotlyLayout } = buildPlotlyFigure(figureInput, theme);
          setResult({
            type: analysisType,
            data: { plotlyData, plotlyLayout, chartType: analysisType },
          } as AnalysisResult);
        } else {
          // Statistics types have no figure, so pass the parsed result through.
          setResult({ type: analysisType, data: parsed } as AnalysisResult);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Analysis failed");
        setResult(null);
      } finally {
        setIsLoading(false);
      }
    },
    [source, theme],
  );

  return { result, isLoading, error, runAnalysis };
}
